import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, CheckCircle, AlertCircle, Loader2, Search, X, BadgeCheck } from 'lucide-react';
import { useMutation, useQuery, gql } from '@apollo/client';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  Slider,
  Checkbox,
  Label,
} from '@artstorefronts/ui';
import AdminLayout from '../../components/AdminLayout';

const ADMIN_COMMUNITY_LIST_QUERY = gql`
  query AdminCommunityListForInvites {
    adminCommunities(limit: 500, offset: 0, sortBy: "name", sortDir: "ASC") {
      communities {
        id
        name
      }
    }
  }
`;

const BULK_INVITE_TO_COMMUNITY = gql`
  mutation BulkInviteToCommunity($input: BulkInviteToCommunityInput!) {
    bulkInviteToCommunity(input: $input) {
      total
      toSend
      queued
      invalid
      alreadyMembers
      alreadyInvited
    }
  }
`;

const COMMUNITY_INVITE_CANDIDATES = gql`
  query CommunityInviteCandidates($input: CommunityInviteCandidatesInput!) {
    communityInviteCandidates(input: $input) {
      userId
      slug
      displayName
      avatarUrl
      similarity
      sampleImages
      city
    }
  }
`;

const BULK_INVITE_ARTISTS_TO_COMMUNITY = gql`
  mutation BulkInviteArtistsToCommunity($input: BulkInviteArtistsToCommunityInput!) {
    bulkInviteArtistsToCommunity(input: $input) {
      total
      toSend
      queued
      invalid
      alreadyMembers
      alreadyInvited
    }
  }
`;

const ADMIN_PROFILE_SEARCH = gql`
  query AdminProfilesForFitAnchor($limit: Int, $offset: Int, $search: String) {
    adminProfiles(limit: $limit, offset: $offset, search: $search) {
      profiles {
        userId
        displayName
        slug
      }
    }
  }
`;

type BulkInviteResult = {
  total: number;
  toSend: number;
  queued: number;
  invalid: string[];
  alreadyMembers: number;
  alreadyInvited: number;
};

type CommunityOption = { id: string; name: string };

const COMMUNITY_ROLES = ['MEMBER', 'MODERATOR', 'ADMIN'] as const;
type CommunityRole = (typeof COMMUNITY_ROLES)[number];

type InvitePath = 'reach' | 'fit';

type GenomeSeedMode = 'MEMBER_CENTROID' | 'ANCHOR' | 'BRIDGE';

const SEED_HINT_KEYS: Record<GenomeSeedMode, string> = {
  MEMBER_CENTROID: 'community-invites.fit-seed-member-centroid-hint',
  ANCHOR: 'community-invites.fit-seed-anchor-hint',
  BRIDGE: 'community-invites.fit-seed-bridge-hint',
};

type InviteCandidate = {
  userId: string;
  slug: string;
  displayName: string;
  avatarUrl: string | null;
  similarity: number;
  sampleImages: string[];
  city: string | null;
};

type AnchorArtist = {
  userId: string;
  displayName: string;
  slug?: string | null;
};

const BULK_INVITE_MAX = 5000;
const FIT_CANDIDATE_LIMIT = 60;
const MIN_IMAGE_OPTIONS = [4, 6, 10] as const;

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

function parseEmailList(text: string): string[] {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const token of text.split(/[\s,;\n\r]+/)) {
    const trimmed = token.trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(trimmed);
  }

  return result;
}

interface CommunitySearchProps {
  communities: CommunityOption[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  loading?: boolean;
}

function CommunitySearch({ communities, selectedId, onSelect, loading }: CommunitySearchProps) {
  const { t } = useTranslation('admin');
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const selectedLabel = useMemo(() => {
    if (!selectedId) return null;
    return communities.find((c) => c.id === selectedId)?.name ?? selectedId;
  }, [communities, selectedId]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return communities;
    return communities.filter((c) => c.name.toLowerCase().includes(q));
  }, [communities, query]);

  const handleSelect = (id: string) => {
    onSelect(id);
    setOpen(false);
    setQuery('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={loading}
          className="w-full max-w-md justify-start font-normal"
        >
          <Search className="mr-2 size-4 shrink-0 opacity-60" />
          <span className="truncate">{selectedLabel ?? t('community-invites.community-placeholder')}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder={t('community-invites.community-search')} value={query} onValueChange={setQuery} />
          <CommandList>
            <CommandEmpty>{t('community-invites.community-no-results')}</CommandEmpty>
            <CommandGroup>
              {filtered.map((c) => (
                <CommandItem key={c.id} value={c.id} onSelect={() => handleSelect(c.id)}>
                  {c.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

interface ReachPaneProps {
  communityId: string | null;
  role: CommunityRole;
}

function ReachPane({ communityId, role }: ReachPaneProps) {
  const { t } = useTranslation('admin');
  const { t: tc } = useTranslation('common');

  const [pasteText, setPasteText] = useState('');
  const [csvText, setCsvText] = useState('');
  const [csvFileName, setCsvFileName] = useState<string | null>(null);
  const [inputTab, setInputTab] = useState<'paste' | 'csv'>('paste');
  const [preview, setPreview] = useState<BulkInviteResult | null>(null);
  const [sendResult, setSendResult] = useState<BulkInviteResult | null>(null);
  const [busy, setBusy] = useState(false);

  const [bulkInvite] = useMutation(BULK_INVITE_TO_COMMUNITY);

  const emails = useMemo(
    () => parseEmailList(inputTab === 'paste' ? pasteText : csvText),
    [inputTab, pasteText, csvText]
  );

  const clearPreview = useCallback(() => {
    setPreview(null);
    setSendResult(null);
  }, []);

  const handlePasteChange = (value: string) => {
    setPasteText(value);
    clearPreview();
  };

  const onCsvDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;

      const file = acceptedFiles[0];
      if (!file.name.toLowerCase().endsWith('.csv')) {
        toast.error(t('community-invites.toast.csv-required'));
        return;
      }

      try {
        const text = await file.text();
        setCsvText(text);
        setCsvFileName(file.name);
        clearPreview();
      } catch {
        toast.error(t('community-invites.toast.csv-read-failed'));
      }
    },
    [clearPreview, t]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: onCsvDrop,
    accept: { 'text/csv': ['.csv'] },
    multiple: false,
    disabled: busy,
  });

  const runBulkInvite = async (dryRun: boolean) => {
    if (!communityId || emails.length === 0) return;

    setBusy(true);
    if (!dryRun) setSendResult(null);

    try {
      const response = await bulkInvite({
        variables: {
          input: {
            communityId,
            emails,
            role,
            dryRun,
          },
        },
      });

      const result = response.data?.bulkInviteToCommunity as BulkInviteResult | undefined;
      if (!result) {
        const message = response.errors?.[0]?.message ?? t('community-invites.toast.request-failed');
        toast.error(message);
        return;
      }

      if (dryRun) {
        setPreview(result);
        setSendResult(null);
      } else {
        setSendResult(result);
        toast.success(t('community-invites.toast.send-success', { count: result.queued }));
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : t('community-invites.toast.request-failed');
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  const previewValid = preview != null ? preview.toSend + preview.alreadyMembers + preview.alreadyInvited : 0;
  const sendSkipped = sendResult != null ? sendResult.alreadyMembers + sendResult.alreadyInvited : 0;
  const sendCapExceeded = preview != null && preview.toSend > BULK_INVITE_MAX;

  const canPreview = Boolean(communityId) && emails.length > 0 && !busy;
  const canSend = preview != null && preview.toSend > 0 && !sendCapExceeded && !busy;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold mb-4">{t('community-invites.email-input')}</h2>
        <Tabs
          value={inputTab}
          onValueChange={(v) => {
            setInputTab(v as 'paste' | 'csv');
            clearPreview();
          }}
        >
          <TabsList>
            <TabsTrigger value="paste">{t('community-invites.tab-paste')}</TabsTrigger>
            <TabsTrigger value="csv">{t('community-invites.tab-csv')}</TabsTrigger>
          </TabsList>
          <TabsContent value="paste" className="mt-4">
            <Textarea
              value={pasteText}
              onChange={(e) => handlePasteChange(e.target.value)}
              placeholder={t('community-invites.paste-placeholder')}
              rows={8}
              className="font-mono text-sm"
            />
          </TabsContent>
          <TabsContent value="csv" className="mt-4">
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-6 sm:p-12 text-center transition-colors
                    ${isDragActive ? 'border-blue-400 bg-blue-50' : 'border-zinc-300 bg-zinc-50'}
                    ${busy ? 'opacity-50 cursor-not-allowed' : 'hover:border-blue-400 hover:bg-blue-50 cursor-pointer'}
                  `}
            >
              <input {...getInputProps()} />
              <div className="flex flex-col items-center justify-center">
                <Upload className="w-12 h-12 mb-4 text-zinc-400" />
                <p className="text-sm text-zinc-700 mb-2">
                  {isDragActive
                    ? t('community-invites.dropzone.drop-here')
                    : t('community-invites.dropzone.drag-or-browse')}
                </p>
                <p className="text-xs text-zinc-500">{t('community-invites.dropzone.csv-only')}</p>
                {csvFileName && (
                  <p className="text-sm text-zinc-600 mt-4">
                    {t('community-invites.csv-loaded', { name: csvFileName, count: emails.length })}
                  </p>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
        {emails.length > 0 && (
          <p className="text-sm text-zinc-500 mt-2">{t('community-invites.parsed-count', { count: emails.length })}</p>
        )}
        {communityId && emails.length === 0 && (
          <p className="text-sm text-amber-700 mt-2">{t('community-invites.hint-empty-list')}</p>
        )}
        {sendCapExceeded && (
          <p className="text-sm text-red-700 mt-2">
            {t('community-invites.hint-send-cap-exceeded', { max: BULK_INVITE_MAX, count: preview!.toSend })}
          </p>
        )}
      </div>

      <div className="flex flex-col sm:flex-row flex-wrap gap-3">
        <Button onClick={() => runBulkInvite(true)} disabled={!canPreview} className="w-full sm:w-auto">
          {busy && !preview ? (
            <>
              <Loader2 className="mr-2 size-4 animate-spin" />
              {t('community-invites.previewing')}
            </>
          ) : (
            t('community-invites.preview')
          )}
        </Button>
        <Button onClick={() => runBulkInvite(false)} disabled={!canSend} variant="default" className="w-full sm:w-auto">
          {busy && preview ? (
            <>
              <Loader2 className="mr-2 size-4 animate-spin" />
              {t('community-invites.sending')}
            </>
          ) : (
            t('community-invites.send')
          )}
        </Button>
      </div>

      {preview && (
        <div className="p-4 rounded-lg border bg-blue-50 border-blue-200">
          <h3 className="text-sm font-medium text-blue-900 mb-3">{t('community-invites.preview-heading')}</h3>
          <dl className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            <div>
              <dt className="text-blue-700">{t('community-invites.count-valid')}</dt>
              <dd className="font-semibold text-blue-900">{previewValid}</dd>
            </div>
            <div>
              <dt className="text-blue-700">{t('community-invites.count-invalid')}</dt>
              <dd className="font-semibold text-blue-900">{preview.invalid.length}</dd>
            </div>
            <div>
              <dt className="text-blue-700">{t('community-invites.count-members')}</dt>
              <dd className="font-semibold text-blue-900">{preview.alreadyMembers}</dd>
            </div>
            <div>
              <dt className="text-blue-700">{t('community-invites.count-invited')}</dt>
              <dd className="font-semibold text-blue-900">{preview.alreadyInvited}</dd>
            </div>
            <div>
              <dt className="text-blue-700">{t('community-invites.count-to-send')}</dt>
              <dd className="font-semibold text-blue-900">{preview.toSend}</dd>
            </div>
          </dl>
          {preview.invalid.length > 0 && (
            <p className="text-xs text-blue-800 mt-3 break-all">
              {t('community-invites.invalid-list', { emails: preview.invalid.join(', ') })}
            </p>
          )}
          {preview.toSend === 0 && (
            <p className="text-sm text-blue-800 mt-3">{t('community-invites.hint-nothing-to-send')}</p>
          )}
        </div>
      )}

      {sendResult && (
        <div className="p-4 rounded-lg border bg-green-50 border-green-200 text-green-800">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-sm">{tc('success')}</p>
              <p className="text-sm mt-2">{t('community-invites.result-queued', { count: sendResult.queued })}</p>
              <p className="text-sm mt-1">{t('community-invites.result-skipped', { count: sendSkipped })}</p>
              {sendResult.invalid.length > 0 && (
                <p className="text-sm mt-1">
                  {t('community-invites.result-invalid', { count: sendResult.invalid.length })}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface ArtistMultiSelectProps {
  selected: AnchorArtist[];
  onChange: (artists: AnchorArtist[]) => void;
  disabled?: boolean;
}

function ArtistMultiSelect({ selected, onChange, disabled }: ArtistMultiSelectProps) {
  const { t } = useTranslation('admin');
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query.trim(), 300);

  const selectedIds = useMemo(() => new Set(selected.map((a) => a.userId)), [selected]);

  const { data, loading } = useQuery(ADMIN_PROFILE_SEARCH, {
    variables: { limit: 20, offset: 0, search: debouncedQuery || undefined },
    skip: !open,
  });

  const profiles: AnchorArtist[] = useMemo(() => {
    const rows = data?.adminProfiles?.profiles ?? [];
    return rows
      .filter((p: { userId?: string; displayName?: string | null }) => p.userId && p.displayName)
      .map((p: { userId: string; displayName: string; slug?: string | null }) => ({
        userId: p.userId,
        displayName: p.displayName,
        slug: p.slug,
      }));
  }, [data]);

  const addArtist = (artist: AnchorArtist) => {
    if (selectedIds.has(artist.userId)) return;
    onChange([...selected, artist]);
    setQuery('');
  };

  const removeArtist = (userId: string) => {
    onChange(selected.filter((a) => a.userId !== userId));
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {selected.map((artist) => (
          <span
            key={artist.userId}
            className="inline-flex items-center gap-1 rounded-full border border-zinc-200 bg-zinc-50 px-2.5 py-1 text-sm"
          >
            {artist.displayName}
            <button
              type="button"
              className="rounded-full p-0.5 hover:bg-zinc-200"
              onClick={() => removeArtist(artist.userId)}
              disabled={disabled}
              aria-label={t('community-invites.fit-anchor-remove', { name: artist.displayName })}
            >
              <X className="size-3.5" />
            </button>
          </span>
        ))}
      </div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" disabled={disabled} className="w-full max-w-md justify-start font-normal">
            <Search className="mr-2 size-4 shrink-0 opacity-60" />
            <span className="truncate">{t('community-invites.fit-anchor-placeholder')}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={t('community-invites.fit-anchor-search')}
              value={query}
              onValueChange={setQuery}
            />
            <CommandList>
              {loading ? (
                <div className="flex items-center justify-center py-6 text-sm text-zinc-500">
                  <Loader2 className="mr-2 size-4 animate-spin" />
                  {t('community-invites.fit-loading')}
                </div>
              ) : (
                <>
                  <CommandEmpty>{t('community-invites.fit-anchor-no-results')}</CommandEmpty>
                  <CommandGroup>
                    {profiles
                      .filter((p) => !selectedIds.has(p.userId))
                      .map((p) => (
                        <CommandItem key={p.userId} value={p.userId} onSelect={() => addArtist(p)}>
                          {p.displayName}
                          {p.slug ? <span className="ml-2 text-zinc-500">@{p.slug}</span> : null}
                        </CommandItem>
                      ))}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function CandidateCardSkeleton() {
  return (
    <div className="rounded-lg border border-zinc-200 p-3 animate-pulse">
      <div className="aspect-[4/3] rounded-md bg-zinc-200 mb-3" />
      <div className="h-4 w-16 rounded bg-zinc-200 mb-2" />
      <div className="h-4 w-3/4 rounded bg-zinc-200" />
    </div>
  );
}

interface FitPaneProps {
  communityId: string | null;
  role: CommunityRole;
  communities: CommunityOption[];
  communitiesLoading?: boolean;
}

function FitPane({ communityId, role, communities, communitiesLoading }: FitPaneProps) {
  const { t } = useTranslation('admin');
  const { t: tc } = useTranslation('common');

  const [seedMode, setSeedMode] = useState<GenomeSeedMode>('MEMBER_CENTROID');
  const [anchorArtists, setAnchorArtists] = useState<AnchorArtist[]>([]);
  const [bridgeCommunityId, setBridgeCommunityId] = useState<string | null>(null);
  const [spread, setSpread] = useState(0);
  const [minImages, setMinImages] = useState<(typeof MIN_IMAGE_OPTIONS)[number]>(4);
  const [humanMadeOnly, setHumanMadeOnly] = useState(true);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [preview, setPreview] = useState<BulkInviteResult | null>(null);
  const [sendResult, setSendResult] = useState<BulkInviteResult | null>(null);
  const [busy, setBusy] = useState(false);

  const debouncedSpread = useDebounce(spread, 300);

  const [bulkInviteArtists] = useMutation(BULK_INVITE_ARTISTS_TO_COMMUNITY);

  const bridgeCommunities = useMemo(
    () => (communityId ? communities.filter((c) => c.id !== communityId) : communities),
    [communities, communityId]
  );

  const anchorUserIds = useMemo(() => anchorArtists.map((a) => a.userId), [anchorArtists]);

  const canFetchCandidates = Boolean(
    communityId &&
    (seedMode === 'MEMBER_CENTROID' ||
      (seedMode === 'ANCHOR' && anchorUserIds.length > 0) ||
      (seedMode === 'BRIDGE' && bridgeCommunityId))
  );

  const candidateInput = useMemo(
    () => ({
      communityId: communityId!,
      mode: seedMode,
      anchorUserIds: seedMode === 'ANCHOR' ? anchorUserIds : undefined,
      bridgeCommunityId: seedMode === 'BRIDGE' && bridgeCommunityId ? bridgeCommunityId : undefined,
      diversity: debouncedSpread / 100,
      minImages,
      humanMadeOnly,
      limit: FIT_CANDIDATE_LIMIT,
    }),
    [communityId, seedMode, anchorUserIds, bridgeCommunityId, debouncedSpread, minImages, humanMadeOnly]
  );

  const {
    data: candidatesData,
    loading: candidatesLoading,
    error: candidatesError,
  } = useQuery(COMMUNITY_INVITE_CANDIDATES, {
    variables: { input: candidateInput },
    skip: !canFetchCandidates,
    fetchPolicy: 'network-only',
  });

  const candidates: InviteCandidate[] = candidatesData?.communityInviteCandidates ?? [];

  const featureDisabled =
    (candidatesError?.graphQLErrors ?? []).some((e) => e.message === 'FEATURE_DISABLED') ||
    (candidatesError?.message?.includes('FEATURE_DISABLED') ?? false);

  // Only nudge about empty results when the query actually succeeded; an error
  // (e.g. FEATURE_DISABLED) must not masquerade as "no matches".
  const showEmptyResults = canFetchCandidates && !candidatesLoading && !candidatesError && candidates.length === 0;

  const relaxedCandidateInput = useMemo(
    () => ({
      ...candidateInput,
      minImages: 0,
      humanMadeOnly: false,
      limit: 1,
    }),
    [candidateInput]
  );

  const { data: relaxedData, loading: relaxedLoading } = useQuery(COMMUNITY_INVITE_CANDIDATES, {
    variables: { input: relaxedCandidateInput },
    skip: !showEmptyResults,
    fetchPolicy: 'network-only',
  });

  const relaxedHasCandidates = (relaxedData?.communityInviteCandidates?.length ?? 0) > 0;

  const showColdStartNudge =
    showEmptyResults && !relaxedLoading && seedMode === 'MEMBER_CENTROID' && !relaxedHasCandidates;
  const showLoosenFilters = showEmptyResults && !relaxedLoading && relaxedHasCandidates;
  const showNoMatchesSeed =
    showEmptyResults && !relaxedLoading && !relaxedHasCandidates && seedMode !== 'MEMBER_CENTROID';

  const clearPreview = useCallback(() => {
    setPreview(null);
    setSendResult(null);
  }, []);

  const handleSeedModeChange = (mode: string) => {
    setSeedMode(mode as GenomeSeedMode);
    setSelectedUserIds(new Set());
    clearPreview();
  };

  const handleSpreadChange = (values: number[]) => {
    const next = values[0];
    if (next == null) return;
    setSpread(next);
    clearPreview();
  };

  const toggleCandidate = (userId: string) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
    clearPreview();
  };

  const selectAllVisible = () => {
    setSelectedUserIds(new Set(candidates.map((c) => c.userId)));
    clearPreview();
  };

  const clearSelection = () => {
    setSelectedUserIds(new Set());
    clearPreview();
  };

  const selectedCount = selectedUserIds.size;
  const selectedArray = useMemo(() => Array.from(selectedUserIds), [selectedUserIds]);

  const runBulkInvite = async (dryRun: boolean) => {
    if (!communityId || selectedArray.length === 0) return;

    setBusy(true);
    if (!dryRun) setSendResult(null);

    try {
      const response = await bulkInviteArtists({
        variables: {
          input: {
            communityId,
            userIds: selectedArray,
            role,
            dryRun,
          },
        },
      });

      const result = response.data?.bulkInviteArtistsToCommunity as BulkInviteResult | undefined;
      if (!result) {
        const message = response.errors?.[0]?.message ?? t('community-invites.toast.request-failed');
        toast.error(message);
        return;
      }

      if (dryRun) {
        setPreview(result);
        setSendResult(null);
      } else {
        setSendResult(result);
        toast.success(t('community-invites.toast.send-success', { count: result.queued }));
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : t('community-invites.toast.request-failed');
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  const sendCapExceeded = preview != null && preview.toSend > BULK_INVITE_MAX;
  const canPreview = Boolean(communityId) && selectedCount > 0 && !busy;
  const canInvite = selectedCount > 0 && preview != null && preview.toSend > 0 && !sendCapExceeded && !busy;

  const seedHintKey = SEED_HINT_KEYS[seedMode];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold mb-2">{t('community-invites.fit-seed-mode-label')}</h2>
        <Tabs value={seedMode} onValueChange={handleSeedModeChange}>
          <TabsList className="grid h-auto w-full max-w-lg grid-cols-1 gap-1 sm:grid-cols-3 sm:gap-0">
            <TabsTrigger value="MEMBER_CENTROID" className="text-xs sm:text-sm">
              {t('community-invites.fit-seed-member-centroid')}
            </TabsTrigger>
            <TabsTrigger value="ANCHOR" className="text-xs sm:text-sm">
              {t('community-invites.fit-seed-anchor')}
            </TabsTrigger>
            <TabsTrigger value="BRIDGE" className="text-xs sm:text-sm">
              {t('community-invites.fit-seed-bridge')}
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <p className="text-sm text-zinc-600 mt-3">{t(seedHintKey)}</p>
      </div>

      {seedMode === 'ANCHOR' && (
        <div>
          <h3 className="text-sm font-medium mb-2">{t('community-invites.fit-anchor-label')}</h3>
          <ArtistMultiSelect
            selected={anchorArtists}
            onChange={(next) => {
              setAnchorArtists(next);
              setSelectedUserIds(new Set());
              clearPreview();
            }}
            disabled={!communityId || busy}
          />
          {communityId && anchorArtists.length === 0 && (
            <p className="text-sm text-amber-700 mt-2">{t('community-invites.fit-hint-anchor-required')}</p>
          )}
        </div>
      )}

      {seedMode === 'BRIDGE' && (
        <div>
          <h3 className="text-sm font-medium mb-2">{t('community-invites.fit-bridge-label')}</h3>
          <CommunitySearch
            communities={bridgeCommunities}
            selectedId={bridgeCommunityId}
            onSelect={(id) => {
              setBridgeCommunityId(id);
              setSelectedUserIds(new Set());
              clearPreview();
            }}
            loading={communitiesLoading}
          />
          {communityId && !bridgeCommunityId && (
            <p className="text-sm text-amber-700 mt-2">{t('community-invites.fit-hint-bridge-required')}</p>
          )}
        </div>
      )}

      <div className="flex flex-col gap-6 lg:flex-row lg:flex-wrap">
        <div className="flex-1 min-w-[200px] space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">{t('community-invites.fit-spread-label')}</span>
            <span className="text-zinc-500 text-xs">
              {spread === 0
                ? t('community-invites.fit-spread-tight')
                : spread === 100
                  ? t('community-invites.fit-spread-diverse')
                  : `${spread}%`}
            </span>
          </div>
          <Slider value={[spread]} onValueChange={handleSpreadChange} min={0} max={100} step={5} disabled={busy} />
        </div>

        <div className="space-y-2">
          <span className="text-sm font-medium">{t('community-invites.fit-min-images-label')}</span>
          <Select
            value={String(minImages)}
            onValueChange={(v) => {
              setMinImages(Number(v) as (typeof MIN_IMAGE_OPTIONS)[number]);
              clearPreview();
            }}
            disabled={busy}
          >
            <SelectTrigger className="w-24">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MIN_IMAGE_OPTIONS.map((n) => (
                <SelectItem key={n} value={String(n)}>
                  {n}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex flex-wrap gap-6">
        <div className="flex items-center gap-2">
          <Checkbox id="fit-gate-public" checked disabled aria-readonly="true" />
          <Label htmlFor="fit-gate-public" className="text-sm font-normal text-zinc-700">
            {t('community-invites.fit-gate-public')}
          </Label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="fit-gate-human-made"
            checked={humanMadeOnly}
            onCheckedChange={(checked) => {
              setHumanMadeOnly(checked === true);
              clearPreview();
            }}
            disabled={busy}
          />
          <Label htmlFor="fit-gate-human-made" className="text-sm font-normal text-zinc-700">
            {t('community-invites.fit-gate-human-made')}
          </Label>
        </div>
      </div>

      {!communityId && <p className="text-sm text-amber-700">{t('community-invites.fit-hint-select-community')}</p>}

      {canFetchCandidates && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-sm text-zinc-700">
              {t('community-invites.fit-selection-bar', {
                selected: selectedCount,
                eligible: candidates.length,
              })}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={selectAllVisible}
                disabled={candidates.length === 0 || busy}
              >
                {t('community-invites.fit-select-all')}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={clearSelection}
                disabled={selectedCount === 0 || busy}
              >
                {t('community-invites.fit-clear-selection')}
              </Button>
            </div>
          </div>

          {featureDisabled && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900">
              <div className="flex items-start gap-3">
                <AlertCircle className="mt-0.5 size-5 shrink-0" aria-hidden="true" />
                <p className="text-sm">{t('community-invites.fit-feature-disabled')}</p>
              </div>
            </div>
          )}

          {candidatesLoading || (showEmptyResults && relaxedLoading) ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <CandidateCardSkeleton key={i} />
              ))}
            </div>
          ) : showColdStartNudge ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900">
              <div className="flex items-start gap-3">
                <AlertCircle className="mt-0.5 size-5 shrink-0" aria-hidden="true" />
                <div>
                  <p className="text-sm font-medium">{t('community-invites.fit-empty-centroid-title')}</p>
                  <p className="mt-1 text-sm text-amber-800">{t('community-invites.fit-empty-centroid-body')}</p>
                </div>
              </div>
            </div>
          ) : showLoosenFilters ? (
            <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-4 text-zinc-800">
              <div className="flex items-start gap-3">
                <AlertCircle className="mt-0.5 size-5 shrink-0 text-zinc-500" aria-hidden="true" />
                <div>
                  <p className="text-sm font-medium">{t('community-invites.fit-no-matches-title')}</p>
                  <p className="mt-1 text-sm text-zinc-600">{t('community-invites.fit-no-matches-loosen-filters')}</p>
                </div>
              </div>
            </div>
          ) : showNoMatchesSeed ? (
            <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-4 text-zinc-800">
              <div className="flex items-start gap-3">
                <AlertCircle className="mt-0.5 size-5 shrink-0 text-zinc-500" aria-hidden="true" />
                <div>
                  <p className="text-sm font-medium">{t('community-invites.fit-no-matches-title')}</p>
                  <p className="mt-1 text-sm text-zinc-600">{t('community-invites.fit-no-matches-seed')}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {candidates.map((candidate) => {
                const isSelected = selectedUserIds.has(candidate.userId);
                const thumb = candidate.sampleImages[0] ?? candidate.avatarUrl;
                const worksCount = candidate.sampleImages.length;
                return (
                  <button
                    key={candidate.userId}
                    type="button"
                    onClick={() => toggleCandidate(candidate.userId)}
                    aria-pressed={isSelected}
                    className={`rounded-lg border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                        : 'border-zinc-200 bg-white hover:border-zinc-300'
                    }`}
                  >
                    <div className="relative aspect-[4/3] rounded-md overflow-hidden bg-zinc-100 mb-3">
                      {thumb ? (
                        <img src={thumb} alt="" className="size-full object-cover" />
                      ) : (
                        <div className="size-full flex items-center justify-center text-zinc-400 text-sm">—</div>
                      )}
                      <span className="absolute top-2 right-2 rounded-full bg-black/70 px-2 py-0.5 text-xs font-medium text-white">
                        {candidate.similarity.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex items-start gap-1.5">
                      <p className="font-medium text-sm text-zinc-900 truncate flex-1">{candidate.displayName}</p>
                      {humanMadeOnly && <BadgeCheck className="size-4 shrink-0 text-emerald-600" aria-hidden="true" />}
                    </div>
                    <p className="text-xs text-zinc-500 mt-1 truncate">
                      @{candidate.slug}
                      {candidate.city ? ` · ${candidate.city}` : ''}
                      {worksCount > 0 ? ` · ${t('community-invites.fit-works-count', { count: worksCount })}` : ''}
                    </p>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {sendCapExceeded && (
        <p className="text-sm text-red-700">
          {t('community-invites.hint-send-cap-exceeded', { max: BULK_INVITE_MAX, count: preview!.toSend })}
        </p>
      )}

      {selectedCount === 0 && canFetchCandidates && candidates.length > 0 && (
        <p className="text-sm text-zinc-500">{t('community-invites.fit-hint-nothing-selected')}</p>
      )}

      <div className="flex flex-col flex-wrap gap-3 sm:flex-row">
        <Button onClick={() => runBulkInvite(true)} disabled={!canPreview} className="w-full sm:w-auto">
          {busy && !preview ? (
            <>
              <Loader2 className="mr-2 size-4 animate-spin" />
              {t('community-invites.previewing')}
            </>
          ) : (
            t('community-invites.fit-preview')
          )}
        </Button>
        <Button
          onClick={() => runBulkInvite(false)}
          disabled={!canInvite}
          variant="default"
          className="w-full sm:w-auto"
        >
          {busy && preview ? (
            <>
              <Loader2 className="mr-2 size-4 animate-spin" />
              {t('community-invites.sending')}
            </>
          ) : (
            t('community-invites.fit-invite-selected')
          )}
        </Button>
      </div>

      <p className="text-xs text-zinc-500">{t('community-invites.fit-notification-hint')}</p>

      {preview && (
        <div className="p-4 rounded-lg border bg-blue-50 border-blue-200">
          <h3 className="text-sm font-medium text-blue-900 mb-3">{t('community-invites.fit-preview-heading')}</h3>
          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div>
              <dt className="text-blue-700">{t('community-invites.fit-count-selected')}</dt>
              <dd className="font-semibold text-blue-900">{selectedCount}</dd>
            </div>
            <div>
              <dt className="text-blue-700">{t('community-invites.count-members')}</dt>
              <dd className="font-semibold text-blue-900">{preview.alreadyMembers}</dd>
            </div>
            <div>
              <dt className="text-blue-700">{t('community-invites.count-invited')}</dt>
              <dd className="font-semibold text-blue-900">{preview.alreadyInvited}</dd>
            </div>
            <div>
              <dt className="text-blue-700">{t('community-invites.fit-count-to-invite')}</dt>
              <dd className="font-semibold text-blue-900">{preview.toSend}</dd>
            </div>
            {preview.invalid.length > 0 && (
              <div>
                <dt className="text-blue-700">{t('community-invites.count-invalid')}</dt>
                <dd className="font-semibold text-blue-900">{preview.invalid.length}</dd>
              </div>
            )}
          </dl>
          {preview.toSend === 0 && (
            <p className="text-sm text-blue-800 mt-3">{t('community-invites.hint-nothing-to-send')}</p>
          )}
        </div>
      )}

      {sendResult && (
        <div className="p-4 rounded-lg border bg-green-50 border-green-200 text-green-800">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-sm">{tc('success')}</p>
              <p className="text-sm mt-2">{t('community-invites.fit-result-queued', { count: sendResult.queued })}</p>
              <p className="text-sm mt-1">
                {t('community-invites.fit-result-skipped', {
                  count: sendResult.alreadyMembers + sendResult.alreadyInvited,
                })}
              </p>
              {sendResult.invalid.length > 0 && (
                <p className="text-sm mt-1">
                  {t('community-invites.result-invalid', { count: sendResult.invalid.length })}
                </p>
              )}
              <p className="text-sm mt-1">{t('community-invites.fit-result-notification-hint')}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CommunityInvites() {
  const { t } = useTranslation('admin');

  const [communityId, setCommunityId] = useState<string | null>(null);
  const [role, setRole] = useState<CommunityRole>('MEMBER');
  const [invitePath, setInvitePath] = useState<InvitePath>('reach');

  const { data: communitiesData, loading: communitiesLoading } = useQuery(ADMIN_COMMUNITY_LIST_QUERY);
  const communities: CommunityOption[] = communitiesData?.adminCommunities?.communities ?? [];

  const handleCommunitySelect = (id: string) => {
    setCommunityId(id);
  };

  const handleRoleChange = (value: string) => {
    setRole(value as CommunityRole);
  };

  return (
    <AdminLayout title={t('community-invites.page-title')} description={t('community-invites.page-description')}>
      <div className="space-y-6">
        <div className="bg-white border border-zinc-200 rounded-lg p-4 sm:p-6 space-y-6">
          <div>
            <h2 className="text-lg font-semibold mb-2">{t('community-invites.select-community')}</h2>
            <p className="text-sm text-zinc-600 mb-4">{t('community-invites.select-community-hint')}</p>
            <CommunitySearch
              communities={communities}
              selectedId={communityId}
              onSelect={handleCommunitySelect}
              loading={communitiesLoading}
            />
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-2">{t('community-invites.role-label')}</h2>
            <Select value={role} onValueChange={handleRoleChange}>
              <SelectTrigger className="w-full max-w-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COMMUNITY_ROLES.map((r) => (
                  <SelectItem key={r} value={r}>
                    {t(`community-invites.roles.${r.toLowerCase()}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="bg-white border border-zinc-200 rounded-lg p-4 sm:p-6 space-y-6">
          <div>
            <h2 className="text-lg font-semibold mb-2">{t('community-invites.path-picker-label')}</h2>
            <p className="text-sm text-zinc-600 mb-4">{t('community-invites.path-picker-hint')}</p>
            <Tabs value={invitePath} onValueChange={(v) => setInvitePath(v as InvitePath)}>
              <TabsList className="grid w-full max-w-md grid-cols-2">
                <TabsTrigger value="reach">{t('community-invites.path-tab-reach')}</TabsTrigger>
                <TabsTrigger value="fit">{t('community-invites.path-tab-fit')}</TabsTrigger>
              </TabsList>
              <TabsContent value="reach" className="mt-6">
                <ReachPane key={communityId ?? 'none'} communityId={communityId} role={role} />
              </TabsContent>
              <TabsContent value="fit" className="mt-6">
                <FitPane
                  key={communityId ?? 'none'}
                  communityId={communityId}
                  role={role}
                  communities={communities}
                  communitiesLoading={communitiesLoading}
                />
              </TabsContent>
            </Tabs>
          </div>
        </div>

        {!communityId && (
          <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-800">
            <AlertCircle className="mt-0.5 size-5 shrink-0" />
            <p className="text-sm">
              {invitePath === 'fit'
                ? t('community-invites.fit-hint-select-community')
                : t('community-invites.hint-select-community')}
            </p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default CommunityInvites;
